import { useState } from 'react'
import styled from '@emotion/styled'
import { useTheme } from '@emotion/react'
import { useRouter } from 'next/router'

import { AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext'

import Footer from '@segment/templates/Footer'
import Navigation from '@segment/templates/Navigation'
import GettingStarted from '@segment/templates/GettingStarted'

import Layout from '@segment/next-shared/layout'

import Hero from './components/Hero'
import Quiz from './components/Quiz'
import CalculateResults from './components/CalculateResults'
import OverallResult from './components/OverallResult'
import HeroResult from './components/HeroResult'
import Results from './components/Results'

import useQuizForm from './components/Quiz/useQuizForm'

import type { Result } from './calculateResults'

import ogImage from './assets/OgImage.jpg'

const copy = {
  pageTitle: `Customer Data Maturity Model. Evaluate your data today! | Twilio Segment`,
  pageDescription: `Evaluate your company’s data strategy and we’ll give you actionable and personalized recommendations on how to improve with Twilio Segment’s Customer Data Maturity Model.`,
}

const CustomerDataMaturityEvaluationView = () => {
  const theme = useTheme()
  const quizForm = useQuizForm()
  const [results, setResults] = useState<Result>()
  const router = useRouter()

  const { handleSubmit, formState, getValues } = quizForm

  const { isValid, isSubmitSuccessful } = formState

  const submitHandler = (result: Result) =>
    handleSubmit(() => {
      router.push(`#results`)
      setResults(result)
    })()

  return (
    <AppearanceProvider appearance="twilioLight">
      <Layout
        pageTitle={copy.pageTitle}
        pageDescription={copy.pageDescription}
        pageOG={ogImage}
        bodyBackground={theme.palette.neutrals.white[100]}
      >
        <Navigation />
        <Main id="results">
          {isSubmitSuccessful && results ? (
            <>
              <HeroResult />
              <OverallResult rating={results.rating} />
              <Results
                collectScore={results.collect.score}
                synthesizeScore={results.synthesize.score}
                actScore={results.act.score}
                adaptScore={results.adapt.score}
              />
              <GettingStarted />
            </>
          ) : (
            <>
              <Hero />
              <Quiz form={quizForm} />
              {isValid && <CalculateResults answers={getValues().answers} onSubmit={submitHandler} />}
            </>
          )}
        </Main>
        <Footer />
      </Layout>
    </AppearanceProvider>
  )
}

const Main = styled.main`
  background-color: ${({ theme }) => theme.palette.neutrals.white[100]};
`

export default CustomerDataMaturityEvaluationView
